import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import FadeInSection from './FadeInSection';
import SubscribeModal from './SubscribeModal';
import scrollToTop from '../utils/scrollToTop';
import { trackEvent } from '../utils/analytics';
import { INSIGHTS, INSIGHT_CATEGORIES } from '../constants/insights-data';
import '../styles/Insights.scss';

// Which slot an article was clicked from — featured banner vs the grid below — so the
// two can be compared rather than lumped into one article_click number.
const trackArticleClick = (article, placement) =>
  trackEvent('insights_article_click', {
    article_slug: article.slug,
    article_title: article.title,
    article_category: article.category,
    placement,
  });

const cardVariants = {
  hidden: { opacity: 0, y: 26 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: (i % 3) * 0.09, ease: [0.22, 1, 0.36, 1] },
  }),
};

function ArticleCard({ article, index }) {
  return (
    <motion.div
      custom={index}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
    >
      <Link
        to={`/insights/${article.slug}`}
        className="insights-card"
        onClick={() => { trackArticleClick(article, 'grid'); scrollToTop(); }}
      >
        <div className="insights-card-media">
          {article.image ? (
            <div
              className="insights-card-media-inner"
              style={{ backgroundImage: `url('${article.image}')` }}
            />
          ) : (
            <div className="insights-card-media-inner insights-card-media-placeholder">
              <span>Market Notes</span>
            </div>
          )}
        </div>
        <div className="insights-card-body">
          <div className="insights-card-meta">
            <span className="insights-card-category">{article.category}</span>
          </div>
          <h3 className="insights-card-title">{article.title}</h3>
          <p className="insights-card-excerpt">{article.excerpt}</p>
          <span className="insights-card-date">{article.date}</span>
        </div>
      </Link>
    </motion.div>
  );
}

function Insights() {
  const [category, setCategory] = useState('All');
  const [subscribeOpen, setSubscribeOpen] = useState(false);

  const featured = INSIGHTS.find(a => a.featured) || INSIGHTS[0];
  const rest = INSIGHTS.filter(a => a.id !== featured.id);
  const visible = category === 'All' ? rest : rest.filter(a => a.category === category);

  return (
    <>
      <Helmet>
        <title>Insights | Lily Lai Real Estate</title>
        <meta name="description" content="Notes on buying, selling, and living well in the Bay Area. Market updates, neighborhood guides, and perspectives from Lily Lai, San Francisco Realtor." />
        <link rel="canonical" href="https://www.lilylaisf.com/insights" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.lilylaisf.com/insights" />
        <meta property="og:title" content="Insights | Lily Lai Real Estate" />
        <meta property="og:description" content="Notes on buying, selling, and living well in the Bay Area, from Lily Lai." />
      </Helmet>

      <div className="insights-page">

        {/* Page header */}
        <FadeInSection>
          <header className="insights-hero">
            <p className="eyebrow insights-hero-eyebrow">The Journal · Bay Area Real Estate</p>
            <h1 className="insights-hero-title">Insights</h1>
            <p className="insights-hero-sub">
              Notes on buying, selling, and living well in the Bay Area,<br />
              written with the same care I bring to every client.
            </p>
          </header>
        </FadeInSection>

        {/* Featured article */}
        {featured && (
          <FadeInSection>
            <section className="insights-featured">
              <Link
                to={`/insights/${featured.slug}`}
                className="insights-featured-card"
                onClick={() => { trackArticleClick(featured, 'featured'); scrollToTop(); }}
                style={{
                  backgroundImage:
                    `linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.28) 55%, rgba(0,0,0,0.12) 100%)` +
                    (featured.image ? `, url('${featured.image}')` : ''),
                }}
              >
                <div className="insights-featured-content">
                  <div className="insights-featured-tags">
                    <span className="insights-featured-badge">{featured.category}</span>
                    <span className="insights-featured-date">Featured · {featured.date}</span>
                  </div>
                  <h2 className="insights-featured-title">{featured.title}</h2>
                  <p className="insights-featured-excerpt">{featured.excerpt}</p>
                  <span className="insights-featured-read">Read Article →</span>
                </div>
              </Link>
            </section>
          </FadeInSection>
        )}

        {/* Filters + grid */}
        <section className="insights-browse">
          <div className="insights-browse-inner">
            <FadeInSection>
              <div className="insights-browse-head">
                <div>
                  <p className="eyebrow">Browse</p>
                  <h2 className="insights-browse-title">
                    {category === 'All' ? 'All Articles' : category}
                  </h2>
                </div>
                <div className="insights-filters">
                  {INSIGHT_CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      className={`insights-filter${category === cat ? ' active' : ''}`}
                      onClick={() => {
                        setCategory(cat);
                        trackEvent('insights_filter_click', { filter_value: cat });
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </FadeInSection>

            {visible.length === 0 ? (
              <div className="insights-empty">
                <p>No articles in this category yet — check back soon.</p>
              </div>
            ) : (
              <div className="insights-grid">
                {visible.map((article, i) => (
                  <ArticleCard key={article.id} article={article} index={i} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Newsletter */}
        <FadeInSection>
          <section className="insights-newsletter">
            <div className="insights-newsletter-inner">
              <p className="eyebrow">Market Notes</p>
              <h2 className="insights-newsletter-title">Delivered Gently</h2>
              <p className="insights-newsletter-sub">
                A few thoughtful emails a month — what's moving in the Bay Area market, and
                what it means for you. No noise, no pressure.
              </p>
              <button
                type="button"
                className="insights-newsletter-btn"
                onClick={() => {
                  trackEvent('newsletter_cta_click', { source: 'insights' });
                  setSubscribeOpen(true);
                }}
              >
                Subscribe
              </button>
              <p className="insights-newsletter-fine">No commitment required · Unsubscribe anytime</p>
            </div>
          </section>
        </FadeInSection>

      </div>

      <SubscribeModal open={subscribeOpen} onClose={() => setSubscribeOpen(false)} />
    </>
  );
}

export default Insights;
